import React, { useEffect, useState, useRef } from 'react'
import Modal from 'react-modal';

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 2300
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    maxHeight: '90vh',
    width: '1000px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    overflowY: 'auto'
  },
};

const ConfirmImageModal = ({ 
  isOpen, 
  onRequestClose, 
  onConfirm, 
  onSkip,
  hideSkipOption = false,
  isHandCarry = false, // true = Air, false = Ship (fallback when no suggestedWarehouse)
  website = '', // Only validate if website is 'Mercari'
  expectedSmileName = null, // Expected recipient name (smileName + orderNo)
  suggestedWarehouse = null, // { address, addressNickName } from routing suggestion — used for dynamic postcode check
  expectedItemNo = null, // 11-digit item number extracted from the order URL
  skipSmileNameOcrCheck = false, // Mercari 郵便 id=9 — no tracking, skip smileName OCR
}) => {
  const [imagePreviews, setImagePreviews] = useState([]);
  const [pastedFiles, setPastedFiles] = useState([]);
  const [skipConfirmation, setSkipConfirmation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // Prevent double submission
  const validationResultRef = useRef(null); // Store validation result
  const validationPromiseRef = useRef(null); // Store the validation promise
  const isValidationRunningRef = useRef(false);
  const isSubmittingRef = useRef(false); // Additional guard against double submission

  // Only enable validation for Mercari orders
  const shouldValidate = website?.toLowerCase() === 'mercari';

  const handlePaste = (event) => {
    if (event.clipboardData && event.clipboardData.items) {
      const items = event.clipboardData.items;
      const newFiles = [];
      const newPreviews = [];

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') === 0) {
          const file = items[i].getAsFile();
          if (file) {
            newFiles.push(file);

            const reader = new FileReader();
            reader.onload = (e) => {
              newPreviews.push(e.target.result);
              if (newPreviews.length === newFiles.length) {
                setImagePreviews((prev) => [...prev, ...newPreviews]);
                setPastedFiles((prev) => [...prev, ...newFiles]);
              }
            };
            reader.readAsDataURL(file);
          }
        }
      }
    }
  };

  // Only listen for paste events when modal is open
  useEffect(() => {
    if (isOpen) {
      resetModal();
      document.addEventListener('paste', handlePaste);
    }
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [isOpen]);

  // Start validation silently in background when image is pasted (only for Mercari)
  // Store the promise so we can await it later if needed
  useEffect(() => {
    if (pastedFiles.length > 0 && shouldValidate && !isValidationRunningRef.current) {
      isValidationRunningRef.current = true;
      validationResultRef.current = null;

      // Create and store the validation promise
      const validationPromise = import('../../utils/postcodeExtractor')
        .then(({ validatePurchaseConfirmImage }) => {
          return validatePurchaseConfirmImage(
            pastedFiles[0],
            isHandCarry,
            expectedSmileName,
            suggestedWarehouse,
            expectedItemNo,
            { skipSmileNameOcrCheck },
          );
        })
        .then(result => {
          validationResultRef.current = result;
          isValidationRunningRef.current = false;
          return result;
        })
        .catch(error => {
          console.error('Background validation error (non-blocking):', error);
          const errorResult = {
            isValid: false,
            cannotValidate: true,
            error: error.message
          };
          validationResultRef.current = errorResult;
          isValidationRunningRef.current = false;
          return errorResult;
        });

      validationPromiseRef.current = validationPromise;
    }
  }, [pastedFiles, shouldValidate, isHandCarry, skipSmileNameOcrCheck, expectedSmileName, suggestedWarehouse, expectedItemNo]);

  // Confirm handler - ensures validation completes before proceeding
  // Added guards to prevent double submission
  const handleConfirm = async () => {
    // Prevent double submission
    if (isSubmittingRef.current || isProcessing) {
      return;
    }

    const imageFile = pastedFiles[0];
    if (!imageFile) {
      return;
    }

    // Set processing flags immediately to prevent double clicks
    isSubmittingRef.current = true;
    setIsProcessing(true);

    let validationResult = null;

    try {
      // If Mercari, ensure validation completes before proceeding
      // This prevents race conditions where upload finishes before validation
      if (shouldValidate && imageFile) {
        if (validationResultRef.current !== null) {
          // Validation already completed, use cached result
          validationResult = validationResultRef.current;
        } else if (validationPromiseRef.current) {
          // Validation is running - wait for it to complete with reasonable timeout
          try {
            // Wait for validation to complete (max 10 seconds - validation typically takes 2-5 seconds)
            validationResult = await Promise.race([
              validationPromiseRef.current,
              new Promise((resolve) => {
                setTimeout(() => {
                  resolve({
                    isValid: false,
                    cannotValidate: true,
                    error: 'Validation timeout - proceeding without validation result'
                  });
                }, 10000); // 10 second timeout (generous, validation usually 2-5s)
              })
            ]);
            
            // Update cached result if we got it
            if (validationResult) {
              validationResultRef.current = validationResult;
            }
          } catch (error) {
            console.error('Error waiting for validation:', error);
            // Proceed without validation result if error
            validationResult = {
              isValid: false,
              cannotValidate: true,
              error: error.message || 'Validation failed'
            };
          }
        } else {
          // Validation hasn't started - start it now and wait
          // This should rarely happen, but handle it just in case
          try {
            const { validatePurchaseConfirmImage } = await import('../../utils/postcodeExtractor');
            validationResult = await Promise.race([
              validatePurchaseConfirmImage(
                imageFile,
                isHandCarry,
                expectedSmileName,
                suggestedWarehouse,
                expectedItemNo,
                { skipSmileNameOcrCheck },
              ),
              new Promise((resolve) => {
                setTimeout(() => {
                  resolve({
                    isValid: false,
                    cannotValidate: true,
                    error: 'Validation timeout'
                  });
                }, 10000);
              })
            ]);
            validationResultRef.current = validationResult;
          } catch (error) {
            console.error('Error running validation on save:', error);
            validationResult = {
              isValid: false,
              cannotValidate: true,
              error: error.message || 'Validation failed'
            };
          }
        }
      }

      // Pass both file and validation result to parent
      // Parent will store this and show warning AFTER upload completes successfully
      onConfirm(imageFile, validationResult);
      resetModal();
    } catch (error) {
      console.error('Error in handleConfirm:', error);
      // Reset flags on error so user can try again
      isSubmittingRef.current = false;
      setIsProcessing(false);
    }
  };

  const handleSkip = () => {
    if (isSubmittingRef.current || isProcessing) {
      return;
    }
    onSkip();
    resetModal();
  };

  const resetModal = () => {
    setImagePreviews([]);
    setPastedFiles([]);
    setSkipConfirmation(false);
    setIsProcessing(false);
    validationResultRef.current = null;
    validationPromiseRef.current = null;
    isValidationRunningRef.current = false;
    isSubmittingRef.current = false;
  };

  const handleClose = () => {
    // Don't allow closing if processing
    if (isSubmittingRef.current || isProcessing) {
      return;
    }
    resetModal();
    onRequestClose();
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} style={customStyles} ariaHideApp={false}>
      <div className="p-4">
        <h2 className="text-xl font-bold mb-4">Order Confirmation Screenshot</h2>
        
        <p className="text-gray-600 mb-4">
          Please paste the order confirmation screenshot to verify your purchase.
        </p>
        
        <div className='flex mb-4'>
          {imagePreviews.map((preview, index) => (
            <img 
              key={index} 
              src={preview} 
              alt={`Confirmation ${index}`} 
              style={{ maxWidth: '500px', maxHeight: '400px', margin: '5px' }} 
            />
          ))}
          {!imagePreviews.length && (
            <div className="border-2 border-dashed border-gray-300 p-8 text-center w-full">
              <p className="text-gray-500">กด Ctrl+V วางรูปได้เลย</p>
            </div>
          )}
        </div>

        {!hideSkipOption && (
          <div className="flex items-center mb-4">
            <input
              id="skipConfirmation"
              type="checkbox"
              checked={skipConfirmation}
              onChange={(e) => setSkipConfirmation(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
              disabled={isProcessing}
            />
            <label htmlFor="skipConfirmation" className="ml-2 text-sm text-gray-700">
              Skip confirmation image (not recommended)
            </label>
          </div>
        )}

        <div className='flex justify-between'>
          <button 
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          
          <div className="space-x-2">
            {!hideSkipOption && skipConfirmation && (
              <button 
                className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSkip}
                disabled={isProcessing}
              >
                Save Without Image
              </button>
            )}
            <button 
              className="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleConfirm}
              disabled={!pastedFiles.length || isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Save With Image'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmImageModal;
